import React, { useState, useEffect } from 'react';
import {
  Item,
  ItemDefinition,
  RenderProps,
  registry
} from '~/training/sequence/templates/items/Item';
import { CARD_TYPE_SELECTION_PLACEHOLDER } from '~/training/sequence/templates/items/itemIds';
import { useEditorStore, SequenceItem } from '~/shell/Store';
import { AddCardOutline } from '~/training/sequence/templates/items/add-card/AddCardOutline';
import { TypeSelectionModal } from './TypeSelectionModal';
import { getAddCardDisabledState } from './utils';
import { CardTemplate } from './templates/types';
import { GLOBAL_PLACEHOLDER } from './templates/constants';
import { fieldRegistry } from './edit-modal/fieldRegistry';
import './edit-modal/registerFields';

interface Props {
  sequenceId: string | number;
  text?: string;
  width?: string;
  height?: string;
  cardTemplates: CardTemplate[];
}

export type CardTypeSelectionPlaceholderDefinition = ItemDefinition<
  typeof CARD_TYPE_SELECTION_PLACEHOLDER,
  Props
>;

export class CardTypeSelectionPlaceholder extends Item<
  typeof CARD_TYPE_SELECTION_PLACEHOLDER,
  Props
> {
  protected render({
    text = 'Add Card',
    width = '268px',
    height = '376px',
    cardTemplates
  }: RenderProps<Props>) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const {
      addSubItem,
      sequence,
      editorMode,
      globalPlaceholders,
      globalPlaceholdersLoading,
      globalPlaceholdersError
    } = useEditorStore();

    const disabledState = getAddCardDisabledState({
      globalPlaceholders,
      globalPlaceholdersLoading,
      globalPlaceholdersError
    });

    useEffect(() => {
      if (!editorMode) return;

      if (globalPlaceholdersError) {
        console.error(
          '[CardTypeSelectionPlaceholder] Global placeholders failed to load:',
          globalPlaceholdersError
        );
      }

      if (!globalPlaceholdersLoading && globalPlaceholders) {
        const hasVerticalImage = !!globalPlaceholders?.images?.vertical;
        const hasAudio = !!globalPlaceholders?.audio;

        const hasNonTextCards = cardTemplates.some(t => t.type !== 'text');
        if (!hasVerticalImage && hasNonTextCards) {
          console.error(
            '[CardTypeSelectionPlaceholder] Required vertical image placeholder is missing'
          );
        }

        const hasImageAudioCards = cardTemplates.some(
          t => t.type === 'imageAudio'
        );
        if (!hasAudio && hasImageAudioCards) {
          console.error(
            '[CardTypeSelectionPlaceholder] Required audio placeholder is missing'
          );
        }
      }
    }, [
      editorMode,
      globalPlaceholders,
      globalPlaceholdersLoading,
      globalPlaceholdersError,
      cardTemplates
    ]);

    const handleSelectTemplate = (template: CardTemplate) => {
      setIsModalOpen(false);

      const tempId = `temp-${Date.now()}`;

      const fieldData: Record<string, unknown> = {};

      template.fields.forEach(field => {
        if (!field.defaultValue) {
          return;
        }

        if (field.defaultValue === GLOBAL_PLACEHOLDER) {
          const resolver = fieldRegistry.getPlaceholderResolver(field.type);
          if (resolver) {
            const resolved = resolver(
              field,
              globalPlaceholders as unknown as Record<string, unknown>
            );
            if (resolved) {
              fieldData[field.name] = [resolved];
            }
          }
        } else {
          const value = field.defaultValue;
          if (Array.isArray(value)) {
            fieldData[field.name] = value;
          } else if (typeof value === 'object' && value !== null) {
            fieldData[field.name] = [value];
          } else {
            fieldData[field.name] = value;
          }
        }
      });

      const existingCardPositions = (sequence!.items || [])
        .filter(
          (item: SequenceItem) =>
            item.__typename === 'Card' || item.typename === 'Card'
        )
        .map((item: SequenceItem) =>
          typeof item.position === 'number' ? item.position : 0
        );

      const maxPosition =
        existingCardPositions.length > 0
          ? Math.max(...existingCardPositions)
          : 0;

      const position = maxPosition + 1;

      const apiData: Record<string, unknown> = {
        type: 'CARD',
        position,
        parent_id: null
      };

      template.fields.forEach(field => {
        const currentValue = fieldData[field.name];
        const apiSerializer = fieldRegistry.getApiSerializer(field.type);
        if (apiSerializer) {
          apiSerializer(field, currentValue, apiData);
        }
      });

      const newItem: Record<string, unknown> = {
        id: tempId,
        __typename: 'Card',
        position
      };

      template.fields.forEach(field => {
        const currentValue = fieldData[field.name];
        const storeSerializer = fieldRegistry.getStoreSerializer(field.type);
        if (storeSerializer) {
          storeSerializer(field, currentValue, newItem);
        }
      });

      addSubItem({
        sequenceId: sequence!.id,
        data: apiData,
        newItem
      });
    };

    if (!editorMode) {
      return null;
    }

    return (
      <>
        <AddCardOutline
          width={width}
          height={height}
          text={text}
          onClick={() => setIsModalOpen(true)}
          disabled={disabledState.disabled}
          title={disabledState.reason}
        />

        <TypeSelectionModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSelectTemplate={handleSelectTemplate}
          cardTemplates={cardTemplates}
        />
      </>
    );
  }
}

registry[CARD_TYPE_SELECTION_PLACEHOLDER] = CardTypeSelectionPlaceholder;
